'use client'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { SampleTag } from './primitives'
import { useDemoLoop, useInView, useTween } from './motion-hooks'

// Product illustration: a repair case as a process line (Modern Treasury connector style).
// Steps mirror the app flow: report, contractor assigned, in progress, invoice review, closed.
// Live demo: while on screen the case replays step by step, the invoice amount counts up and
// a shimmer runs along the segment that waits for approval. Pauses under the pointer.
// Resting state (server render, reduced motion): invoice review is the current step.
const STEPS = [
  { title: 'Gemeldet', detail: 'Mieterin · 3 Fotos', meta: '14.09.2026' },
  {
    title: 'Handwerker beauftragt',
    detail: 'Muster Sanitär AG',
    meta: '15.09.2026',
  },
  { title: 'In Arbeit', detail: 'Termin vor Ort', meta: '18.09.2026' },
  { title: 'Rechnung prüfen', detail: null, meta: 'per Link hochgeladen' },
  { title: 'Abgeschlossen', detail: 'nach Ihrer Freigabe', meta: '' },
]
const REST_STEP = 3
const INVOICE_CHF = 1342.5

// Phase 0 is the resting state; the loop retracts and replays from phase 1.
const PHASES = [
  { current: REST_STEP, hold: 2400 },
  { current: -1, hold: 650 },
  { current: 0, hold: 900 },
  { current: 1, hold: 900 },
  { current: 2, hold: 900 },
  { current: REST_STEP, hold: 4600 },
]

const MARKER = {
  done: 'border-signal bg-signal',
  current: 'border-warning bg-surface ring-4 ring-warning-soft',
  todo: 'border-rule-strong bg-surface',
}

const amount = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export function RepairLine({ className }) {
  const ref = useRef(null)
  const pausedRef = useRef(false)
  const inView = useInView(ref)
  const phase = useDemoLoop(PHASES, { active: inView, pausedRef, loopFrom: 1 })
  const current = PHASES[phase].current
  const invoice = useTween(current >= REST_STEP ? INVOICE_CHF : 0, 900)
  const statusTitle = STEPS[Math.max(current, 0)].title

  const pause = value => () => {
    pausedRef.current = value
  }

  return (
    <figure
      ref={ref}
      onMouseEnter={pause(true)}
      onMouseLeave={pause(false)}
      onFocus={pause(true)}
      onBlur={pause(false)}
      className={cn(
        'rounded-card border border-rule-strong bg-surface',
        className
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-rule px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <p className="text-base font-semibold text-ink">
            Wasserschaden Badezimmer
          </p>
          <p className="font-mono text-xs text-graphite">
            Meldung #1042 · Einheit 014-3.1
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-[3px] bg-canvas px-2 py-1 font-mono text-[11px] text-graphite">
            Status
            <span key={statusTitle} className="fade-up font-medium text-ink">
              {statusTitle}
            </span>
          </span>
          <SampleTag />
        </div>
      </div>

      <ol className="flex flex-col px-5 pb-2 pt-6 md:flex-row md:pb-6">
        {STEPS.map((step, i) => {
          const isLast = i === STEPS.length - 1
          const state =
            i < current ? 'done' : i === current ? 'current' : 'todo'
          // Steps after the resting step always show their details; earlier ones appear when reached.
          const showDetails = i <= current || i > REST_STEP
          const detail = step.detail ?? `CHF ${amount.format(invoice)}`
          return (
            <li
              key={step.title}
              aria-current={state === 'current' ? 'step' : undefined}
              className="group/step relative flex min-w-0 flex-1 flex-col gap-0.5 pb-5 pl-8 md:pb-0 md:pl-0 md:pr-4 md:pt-7"
            >
              {!isLast && (
                <span
                  aria-hidden
                  className="absolute left-[5px] top-3 h-full w-[1.5px] overflow-hidden bg-rule-strong md:left-3 md:right-0 md:top-[5px] md:h-[1.5px] md:w-auto"
                >
                  <span
                    className={cn(
                      'absolute inset-0 origin-top bg-signal transition-transform duration-700 ease-out md:origin-left',
                      i < current
                        ? 'scale-100'
                        : 'scale-y-0 md:scale-x-0 md:scale-y-100'
                    )}
                  />
                  {state === 'current' && (
                    <span className="scan absolute inset-y-0 left-0 hidden w-1/4 bg-gradient-to-r from-transparent via-warning to-transparent motion-reduce:hidden md:block" />
                  )}
                </span>
              )}
              {state === 'current' && (
                <span
                  aria-hidden
                  className="ping-soft absolute left-0 top-0.5 h-3 w-3 rounded-[2px] border-[1.5px] border-warning md:top-0"
                />
              )}
              <span
                aria-hidden
                className={cn(
                  'absolute left-0 top-0.5 h-3 w-3 rounded-[2px] border-[1.5px] transition-[background-color,border-color,box-shadow,transform] duration-300 group-hover/step:scale-125 md:top-0',
                  MARKER[state]
                )}
              />
              <span className="text-sm font-semibold text-ink transition-colors duration-200 group-hover/step:text-signal">
                {step.title}
              </span>
              <span
                className={cn(
                  'flex flex-col gap-0.5 transition-opacity duration-500',
                  showDetails ? 'opacity-100' : 'opacity-0'
                )}
              >
                <span
                  className={cn(
                    'font-mono text-xs tabular-nums',
                    state === 'current' ? 'text-warning' : 'text-ink'
                  )}
                >
                  {detail}
                </span>
                {step.meta && (
                  <span className="font-mono text-[11px] text-graphite">
                    {step.meta}
                  </span>
                )}
              </span>
              <span className="sr-only">
                {state === 'done'
                  ? '(erledigt)'
                  : state === 'current'
                    ? '(aktueller Schritt)'
                    : '(offen)'}
              </span>
            </li>
          )
        })}
      </ol>
    </figure>
  )
}
